/**
 * CanvasDepartChoixVoile — le voile « Sur quel papier à en-tête commencer ? » posé par-dessus une
 * page neuve que l'avocat vient de demander.
 *
 * **Pourquoi un voile, et pas un écran qui précède le document.** Un montage de canvas est borné
 * (`CANVAS_MOUNT_WAIT_MS`, `DEFAULT_HYDRATION_TIMEOUT_MS`) : il ne peut pas attendre un clic. Le
 * document se monte donc tout de suite sur le défaut du cabinet, comme toujours, et ce voile le
 * recouvre. Choisir le défaut l'enlève ; choisir autre chose remplace le document par une page
 * neuve partie du modèle choisi (`ouvrirUnDocumentVierge` avec son `depart`).
 *
 * ⚠️ Rendu par `RedactionPanel`, en absolu par-dessus la zone du canvas : ni à la place de
 * `RedactionCanvas` (SuperDoc ne se construirait pas, la session n'atteindrait jamais `ready`),
 * ni à l'intérieur (un enfant positionné y change ce que le peintre mesure).
 *
 * Il ne s'adresse qu'à l'avocat devant une page neuve vide. Dès que ce n'est plus le cas, il se
 * clôt sans choix : Hector prend le document, un autre document y est posé, l'écran passe à une
 * autre conversation, ou le cabinet n'a aucun papier à en-tête à proposer.
 */

import * as React from 'react';
import { toast } from 'sonner';
import type { CanvasDocumentDepart } from '@hector/shared-types';
import { useCabinetId, useCabinetMemberMe } from '@/hooks/useCabinetMemberMe';
import { useLetterheads } from '@/hooks/useLetterheads';
import { useConversationAffichee } from '@/features/hector-agent-casediscovery-v3/state/conversationAffichee';
import { decrireLeRefusDeSortie, ouvrirUnDocumentVierge } from '../state/canvasBasculeDeBrouillon';
import { cloreLeChoixDeDepart, useChoixDeDepartPropose } from '../state/canvasChoixDeDepart';
import { useCanvasDocumentPresentation } from '../state/canvasDocumentScopeContext';
import { useCanvasSessionProtectedByWork } from '../state/canvasSessionProtection';
import { CanvasDepartChoix, type PapierDeDepart } from './CanvasDepartChoix';

const AUCUN_PAPIER: readonly PapierDeDepart[] = [];

export function CanvasDepartChoixVoile({ conversationId }: { conversationId: string }): React.JSX.Element | null {
  const propose = useChoixDeDepartPropose(conversationId);
  const cabinetId = useCabinetId();
  const { isLoading: cabinetEnCoursDeLecture } = useCabinetMemberMe();
  // La liste n'est lue que pour un voile proposé : un panneau sans choix en attente ne la demande pas.
  const { data, isError } = useLetterheads(propose ? cabinetId : null);
  const protegeParUnTravail = useCanvasSessionProtectedByWork(conversationId);
  const presentation = useCanvasDocumentPresentation();
  const conversationAffichee = useConversationAffichee();
  const [occupe, setOccupe] = React.useState(false);

  const papiers = React.useMemo<readonly PapierDeDepart[]>(
    () => data?.letterheads
      .filter((enTete) => enTete.hasDocument)
      .map(({ id, name, usageNote, isDefault }) => ({ id, name, usageNote, isDefault }))
      ?? AUCUN_PAPIER,
    [data],
  );

  // Ce document n'est plus une page neuve vide de l'avocat, ou l'avocat ne la regarde plus.
  const sansObjet = protegeParUnTravail
    || presentation.templateMeta !== null
    || presentation.activeDraft !== null
    || (conversationAffichee !== null && conversationAffichee !== conversationId);
  // Rien à proposer : la page neuve reste sur le défaut, comme avant ce voile. Une liste encore
  // en lecture n'entre pas ici, elle se tait seulement.
  const rienAProposer = (data !== undefined && papiers.length === 0)
    || isError
    || (cabinetId === null && !cabinetEnCoursDeLecture);

  React.useEffect(() => {
    if (propose && (sansObjet || rienAProposer)) cloreLeChoixDeDepart(conversationId);
  }, [propose, sansObjet, rienAProposer, conversationId]);

  const choisir = React.useCallback(async (papierId: string | null) => {
    const papier = papierId === null ? null : papiers.find((candidat) => candidat.id === papierId);
    // Le document monté sous le voile est déjà parti du papier par défaut : rien à remplacer.
    if (papier?.isDefault) {
      cloreLeChoixDeDepart(conversationId);
      return;
    }
    if (papier === undefined) return;
    const depart: CanvasDocumentDepart = papier === null
      ? { kind: 'aucun' }
      : { kind: 'nomme', letterheadId: papier.id, name: papier.name };
    const libelle = papier === null ? 'la page blanche' : `le papier à en-tête « ${papier.name} »`;
    setOccupe(true);
    try {
      const issue = await ouvrirUnDocumentVierge(conversationId, undefined, depart);
      switch (issue.issue) {
        case 'vierge':
          cloreLeChoixDeDepart(conversationId);
          return;
        case 'protege':
          toast.error(
            `Hector rédige dans ce document : ${libelle} n'a pas été appliqué. `
            + 'Le document reste sur le papier à en-tête par défaut du cabinet.',
          );
          return;
        case 'montage-remplace':
          toast.error(
            `Le canvas a changé pendant l'application de ${libelle} : le document n'a pas été remplacé. `
            + 'Choisissez à nouveau le papier à en-tête.',
          );
          return;
        case 'refus-de-sortie':
          toast.error(decrireLeRefusDeSortie(issue.refus));
      }
    } finally {
      setOccupe(false);
    }
  }, [conversationId, papiers]);

  if (!propose || sansObjet || papiers.length === 0) return null;

  return (
    <div
      className="absolute inset-0 z-20 overflow-y-auto bg-stone-50"
      data-testid="canvas-depart-choix"
    >
      <CanvasDepartChoix
        papiers={papiers}
        occupe={occupe}
        onChoisir={(papierId) => { void choisir(papierId); }}
      />
    </div>
  );
}
